import {
  ParcelConditionKind,
  parcelConditionKindToLabel,
} from "@redotech/redo-conversion-core-common/checkouts/parcel/parcel-tree";
import { RcoCheckoutPackageConditionStep } from "@redotech/redo-flows-common/advanced-flow-db-parser";
import { useTranslation } from "@redotech/redo-i18n";
import BranchIcon from "@redotech/redo-icons/git-branch-01.svg";
import { RedoFeaturedIcon } from "@redotech/redo-web/arbiter-components/featured-icon/redo-featured-icon";
import { Flex } from "@redotech/redo-web/flex";
import { assertNever } from "@redotech/util/type";
import { Handle, Position } from "@xyflow/react";
import { memo } from "react";
import { PartialWithRequiredType } from "../steps";
import { BaseNode } from "./base-node";
import { FieldToListMatchDetails } from "./common/field-to-list-match-details";
import { IntegerComparisonDetails } from "./common/integer-comparison-details";
import { WeightComparisonDetails } from "./common/weight-comparison-details";
import * as css from "./node.module.css";

export interface RcoCheckoutPackageConditionNodeData {
  step: PartialWithRequiredType<RcoCheckoutPackageConditionStep>;
  deleteElements: (id: string) => void;
}

export const RcoCheckoutPackageConditionNode = memo(
  function RcoCheckoutPackageConditionNode(data: {
    id: string;
    data: RcoCheckoutPackageConditionNodeData;
    selected?: boolean | undefined;
  }) {
    const { t } = useTranslation();

    return (
      <BaseNode
        icon={<RedoFeaturedIcon color="warning" Icon={BranchIcon} size="xs" />}
        onDelete={() => data.data.deleteElements(data.id)}
        selected={data.selected}
        step={data.data.step as any}
        title={t("Condition")}
      >
        <Handle position={Position.Top} type="target" />
        <Flex className={css.fullInput} fontSize="xs" gap="none" wrap="wrap">
          <ConditionDetails step={data.data.step} />
        </Flex>
        <Handle id="a" position={Position.Bottom} type="source" />
        <Handle id="b" position={Position.Bottom} type="source" />
      </BaseNode>
    );
  },
);

const ConditionDetails = memo(function ConditionDetails({
  step,
}: {
  step: PartialWithRequiredType<RcoCheckoutPackageConditionStep>;
}) {
  switch (step.condition?.kind) {
    case ParcelConditionKind.WEIGHT_OZ:
      return (
        <WeightComparisonDetails
          field={parcelConditionKindToLabel[ParcelConditionKind.WEIGHT_OZ]}
          matchType={step.condition.matchType}
          matchValue={step.condition.matchValue}
        />
      );
    case ParcelConditionKind.ITEM_COUNT:
      return (
        <IntegerComparisonDetails
          field={parcelConditionKindToLabel[ParcelConditionKind.ITEM_COUNT]}
          matchType={step.condition.matchType}
          matchValue={step.condition.matchValue}
        />
      );
    case ParcelConditionKind.PRODUCT_TAGS:
      return (
        <FieldToListMatchDetails
          field={parcelConditionKindToLabel[ParcelConditionKind.PRODUCT_TAGS]}
          fieldLabelIsPlural={false}
          matchType={step.condition.matchType}
          matchValues={step.condition.matchValues}
        />
      );
    case undefined:
      return null;
    default:
      assertNever(step.condition);
  }
});
